import { departmentsQuery } from "@common/api/departments"
import { useExportUsersMutation, useUsers } from "@common/api/users"
import type { RoleLabelPublic, UserPublic } from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { Button } from "@common/components/ui/button"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { GridTable } from "@common/components/ui/grid-table"
import { Skeleton } from "@common/components/ui/skeleton"
import { StatusDot } from "@common/components/ui/status-dot"
import { useInfiniteScroll } from "@common/hooks/use-infinite-scroll"
import { useHasPermission } from "@common/hooks/use-permissions"
import { formatMonth } from "@common/lib/date-utils"
import { cn } from "@common/lib/utils"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { RotateCw, TriangleAlert, Users } from "lucide-react"
import { useCallback } from "react"
import { FilterBar } from "@/components/layout/filter-bar"
import { FilterButton } from "@/components/layout/filter-button"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import useCustomToast from "@/hooks/use-custom-toast"
import { PanelMenu } from "@/routes/_layout/settings/_panel/-components/panel-menu"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"
import { UserFilterBar } from "@/routes/_layout/settings/-components/user-filter-bar"
import { userFilterConfigs } from "@/routes/_layout/settings/-components/user-filter-config"
import { downloadUsersCsv } from "@/routes/_layout/settings/-lib/download-users-csv"
import { AUTHORITY_ROLES } from "@/routes/_layout/settings/-lib/roles"
import {
  type UserFilterValues,
  userFilterSchema,
} from "@/routes/_layout/settings/-lib/user-filters"
import { displayName, initialsName } from "../-lib/display-name"
import { memberListParams } from "../-lib/list-params"

const COLUMNS =
  "grid-cols-[minmax(12rem,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto_auto]"

const JOINED = "whitespace-nowrap pr-6 text-small-regular text-muted-foreground"

const EMAIL = "truncate text-extra-small-regular text-greyscale-grey"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/members/_list",
)({
  validateSearch: (search) => userFilterSchema.parse(search),
  component: MembersPage,
})

function MembersPage(): React.ReactElement {
  const filters = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const applyFilter = useCallback(
    (patch: Partial<UserFilterValues>) =>
      navigate({ search: (previous) => ({ ...previous, ...patch }) }),
    [navigate],
  )
  const applySearch = useCallback(
    (next: string) =>
      navigate({
        search: (previous) => ({ ...previous, search: next || undefined }),
        replace: true,
      }),
    [navigate],
  )

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar
          filters={filters}
          onSearch={applySearch}
          applyFilter={applyFilter}
        />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <UserFilterBar values={filters} onChange={applyFilter} />
          <MemberResults filters={filters} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({
  filters,
  onSearch,
  applyFilter,
}: {
  filters: UserFilterValues
  onSearch: (next: string) => void
  applyFilter: (patch: Partial<UserFilterValues>) => void
}): React.ReactElement {
  const canCreate = useHasPermission("users:create")
  const exportUsers = useExportUsersMutation()
  const showToast = useCustomToast()

  const runExport = () =>
    exportUsers.mutate(memberListParams(filters), {
      onSuccess: downloadUsersCsv,
      onError: (error) => handleError(error, showToast),
    })

  return (
    <ListToolbar
      actions={
        <>
          <NameFilter
            search={filters.search ?? ""}
            onSearch={onSearch}
            placeholder="Search name or email…"
          />
          <FilterButton
            active={hasMenuFilters(filters)}
            configs={userFilterConfigs}
            onChange={applyFilter}
          />
          <PanelMenu label="Member actions">
            <DropdownMenuItem
              disabled={exportUsers.isPending}
              onSelect={(event) => {
                event.preventDefault()
                runExport()
              }}
            >
              {exportUsers.isPending ? "Exporting…" : "Export CSV"}
            </DropdownMenuItem>
          </PanelMenu>
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/members/create">Add member</Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function MemberResults({
  filters,
}: {
  filters: UserFilterValues
}): React.ReactElement {
  const params = useParams({ strict: false })
  const selectedId = "userId" in params ? params.userId : undefined
  const {
    users,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useUsers(memberListParams(filters))
  const sentinelRef = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isError,
    fetchNextPage,
  })

  if (isLoading) {
    return <MemberListSkeleton />
  }

  if (isError && users.length === 0) {
    return <LoadError onRetry={() => refetch()} />
  }

  if (users.length === 0) {
    return <EmptyState filtered={hasFilters(filters)} />
  }

  return (
    <>
      <GridTable className={COLUMNS}>
        <SettingsTableHeader>
          <SettingsTableHead className="pl-6">Member</SettingsTableHead>
          <SettingsTableHead>Role</SettingsTableHead>
          <SettingsTableHead>Departments</SettingsTableHead>
          <SettingsTableHead>Status</SettingsTableHead>
          <SettingsTableHead className="pr-6">Joined</SettingsTableHead>
        </SettingsTableHeader>
        {users.map((user) => (
          <MemberRow
            key={user.id}
            user={user}
            selected={user.id === selectedId}
          />
        ))}
      </GridTable>
      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center p-4">
          {isFetchingNextPage && <Skeleton className="h-6 w-24" />}
        </div>
      )}
    </>
  )
}

function MemberRow({
  user,
  selected,
}: {
  user: UserPublic
  selected: boolean
}): React.ReactElement {
  const active = !!user.is_active

  return (
    <SettingsTableRow asChild aria-selected={selected} selected={selected}>
      <Link
        to="/settings/workspace/members/$userId"
        params={{ userId: user.id }}
        search={true}
        className="text-foreground"
      >
        <SettingsTableCell className="pl-6">
          <Member user={user} active={active} />
        </SettingsTableCell>
        <SettingsTableCell>
          <span className="truncate">{roleLabel(user.role_labels)}</span>
        </SettingsTableCell>
        <SettingsTableCell className="text-muted-foreground">
          <Department departmentId={user.department_id} />
        </SettingsTableCell>
        <SettingsTableCell className="gap-2 whitespace-nowrap">
          <StatusDot tone={active ? "success" : "neutral"} />
          {active ? "Active" : "Deactivated"}
        </SettingsTableCell>
        <SettingsTableCell className={JOINED}>
          {formatMonth(user.created_at)}
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function Member({
  user,
  active,
}: {
  user: UserPublic
  active: boolean
}): React.ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <UserInitials user={initialsName(user)} title={null} />
      <div className="min-w-0">
        <div className={cn("truncate", !active && "text-greyscale-grey")}>
          {displayName(user)}
        </div>
        <div className={EMAIL}>{user.email ?? user.username}</div>
      </div>
    </div>
  )
}

function Department({
  departmentId,
}: {
  departmentId: string | null | undefined
}): React.ReactElement {
  const { data } = useQuery(departmentsQuery())
  const name = data?.data.find((item) => item.id === departmentId)?.name

  return <span className="truncate">{name}</span>
}

function roleLabel(roleLabels: RoleLabelPublic[]): string {
  const roles = roleLabels
    .filter((role) => !AUTHORITY_ROLES.has(role.name))
    .map((role) => role.label)
  return roles.join(" · ")
}

function hasFilters(filters: UserFilterValues): boolean {
  return Boolean(filters.search) || hasMenuFilters(filters)
}

function hasMenuFilters(filters: UserFilterValues): boolean {
  return Boolean(filters.roles?.length || filters.active !== undefined)
}

function EmptyState({ filtered }: { filtered: boolean }): React.ReactElement {
  const title = filtered ? "No matching members" : "No members"
  const body = filtered
    ? "Try a different search or filter."
    : "Add the people who work in your workspace."

  return <ListEmpty icon={Users} title={title} body={body} />
}

function LoadError({ onRetry }: { onRetry: () => void }): React.ReactElement {
  return (
    <ListEmpty
      icon={TriangleAlert}
      title="Couldn't load members"
      body="Something went wrong while loading members."
      action={
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCw />
          Retry
        </Button>
      }
    />
  )
}

function MemberListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3 p-4">
      {[1, 2, 3, 4, 5].map((index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  )
}
